import { clsx } from 'clsx'
import { type ReactNode, useState } from 'react'

import { FvButton, Icon } from '@fv/client-components'

import { FooterBar } from '@/components/FooterBar'
import { LazyImg } from '@/components/LazyImg'
import { PageHeader } from '@/components/PageHeader'
import { Pinline } from '@/components/Pinline'

import { HasPermission } from '../auth/HasPermission'
import { CreateBrandForm } from './CreateBrandForm'
import type { AccountBranding } from './types'

type Props = {
  brands: AccountBranding[]
  selectedId: string | undefined
  onSelect: (brand: AccountBranding) => void
  onClose: () => void
}

export const BrandSelectorPanel = ({
  brands,
  selectedId,
  onSelect,
  onClose,
}: Props) => {
  const [showCreate, setShowCreate] = useState(false)

  if (showCreate) {
    return (
      <CreateBrandForm
        onSuccess={brand => {
          onSelect(brand)
          setShowCreate(false)
        }}
        onCancel={() => setShowCreate(false)}
      />
    )
  }

  const resolvedId = selectedId ?? brands.find(b => b.isDefault)?._id

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="p-6 overflow-auto flex-1">
        <PageHeader title="Select a brand" noBorder />
        <div className="flex-1 overflow-auto">
          <div className="flex flex-col gap-y-3">
            <HasPermission name="manage-branding">
              <BrandListItem theme="create" onClick={() => setShowCreate(true)}>
                <Icon
                  icon="plus"
                  className="text-fv-blueg group-hover:text-fv-gray-dark"
                />
                <span>Create brand</span>
              </BrandListItem>
            </HasPermission>

            {brands.map(brand => (
              <BrandOption
                key={brand._id}
                brand={brand}
                isSelected={brand._id === resolvedId}
                onSelect={() => onSelect(brand)}
              />
            ))}
          </div>
        </div>
      </div>

      <FooterBar>
        <FvButton theme="plain" icon="times" onClick={onClose}>
          Cancel
        </FvButton>
      </FooterBar>
    </div>
  )
}

type BrandOptionProps = {
  brand: AccountBranding
  isSelected: boolean
  onSelect: () => void
}

const BrandOption = ({ brand, isSelected, onSelect }: BrandOptionProps) => (
  <BrandListItem theme={isSelected ? 'selected' : 'default'} onClick={onSelect}>
    <div className="border border-fv-gray p-2 bg-white flex items-center justify-center w-40">
      <LazyImg
        src={brand.logoUri}
        alt={brand.name}
        className="max-h-12 max-w-full object-contain"
      />
    </div>
    <Pinline className="flex-1" />
    <div className="flex items-center">
      {isSelected && (
        <Icon icon="check-circle" className="text-fv-orange-bright" />
      )}
      <span>{brand.name}</span>
      {brand.isDefault && (
        <span className=" text-fv-gray-600 text-sm ml-1 italic">(primary)</span>
      )}
    </div>
  </BrandListItem>
)

type ListItemProps = {
  theme?: 'create' | 'selected' | 'default'
  children: ReactNode
  onClick: () => void
}
const BrandListItem = ({ theme, children, onClick }: ListItemProps) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'group flex items-center rounded-sm border p-3 text-left transition-colors min-h-18 hover:border-fv-blue-300 hover:bg-fv-blue-50',
        theme === 'create' && 'border-dashed border-fv-gray ',
        theme === 'selected' &&
          'border-fv-beer-medium bg-fv-beer-xlight pointer-events-none gap-x-2 ',
        theme === 'default' && 'border-fv-gray gap-x-2 ',
      )}
    >
      {children}
    </button>
  )
}
